import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import type { ValueOf } from '@/helpers/types';
import type { ComputedRef, Ref } from 'vue';

import { MonetizationType } from '@/@types/graphql-types';
import { WebLocale } from '@/enums/web-locale';
import { toValue } from 'vue';
import { computed, onMounted, onServerPrefetch } from 'vue';

import { useTitleDetailsStore } from '@/stores/pinia/useTitleDetailsStore';
import { storeToRefs } from 'pinia';
import { useExperiment } from '@/libraries/experiment/composables/useExperiment';

// TYPES
type digits = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
type day = `${'0'}${Exclude<digits, '0'>}` | `${'1' | '2'}${digits}` | '30' | '31';
type month = `${'0'}${Exclude<digits, '0'>}` | '10' | '11' | '12';
type year = `20${digits}${digits}`;
type ISO8601 = `${year}-${month}-${day}`;

/** Provider promotions are only for titles with Flatrate and free offers of the promo provider. */
const flatrateOrFreeOrAds = [MonetizationType.Flatrate, MonetizationType.Free, MonetizationType.Ads];

interface PromoText {
	/**
	 * Angled ribbon over square provider icons or
	 * drawer below wide provider icons.
	 */
	ribbon?: string;

	/**
	 * Ribbon text exclusively for wide provider icons
	 * which have room for a bit more text.
	 */
	wideRibbon?: string;

	/**
	 * Text below the square provider in GridOffer (next to 4k/quality).
	 * Text above the price in ListOffer.
	 */
	offerText?: string;

	/** Text that appears on the backdrop play button. */
	longText?: string;

	/** Optional promotion start date in `YYYY-MM-DD` format. */
	startDate?: ISO8601;

	/** `YYYY-MM-DD` format. This date is inclusive. */
	endDate: ISO8601;

	/** 3 character shortname of the promoted provider. */
	provider: string;

	/** The Url of the promoted provider which overrides the regular offer link. */
	url?: string;

	/** Disclaimer text - for additional Terms and Conditions, passed into the DisclaimerButton */
	disclaimerText?: string;

	/** Secondary disclaimer text for BuyBoxOfferNew - for additional Terms and Conditions, passed into the DisclaimerButton */
	offerDisclaimerText?: string;

	/** Suppress the provider promotion on the backdrop play button --> was a special requirement from HBO Max GB promotion to only have the buybox offer promotions, but assumption is that this could be more common */
	hideBackdropPromo?: boolean;

	experimentName?: string; // for testing purposes to link the promo to a specific experiment variant
}

/** Normal PromoText config with the Provider Icon URL */
export type PromoTextWithIcon = PromoText & { icon: string };

// Change uk to gb in the WebLocale list and return it upper case to match store country
type PromotionLocale = Uppercase<Exclude<keyof typeof WebLocale, 'uk'> | 'gb'>;

const promotions = {
	DK: [
		{
			ribbon: 'Deal',
			startDate: '2026-10-06',
			endDate: '2026-10-13',
			provider: 'dnp',
			offerText: 'Sidste chance!',
			longText: 'Fra 49 kr./md., når du tilmelder dig i 6 md.',
			disclaimerText:
				'Tilbuddet gælder t.o.m. 13.10.26. Kun gyldigt for nye og berettigede tilbagevendende abonnenter. For personer over 18 år. Prisen på 6-måneders spareabonnement på Standard med reklamer stiger til 69 kr./md fra 14.10.26. Besparelsen sammenligner den nuværende pris på 49 kr./md med den nye pris på 6-måneders spareabonnement på Standard med reklamer på 69 kr./md. Baseret på en bindingsperiode på 6 måneder, hvorefter abonnementet fornys automatisk til 79 kr. pr. måned. Afmeld når som helst med virkning efter bindingsperioden.',
			offerDisclaimerText:
				'Tilbuddet gælder t.o.m. 13.10.26. Kun gyldigt for nye og berettigede tilbagevendende abonnenter. For personer over 18 år. Prisen på 6-måneders spareabonnement på Standard med reklamer stiger til 69 kr./md fra 14.10.26. Besparelsen sammenligner den nuværende pris på 49 kr./md med den nye pris på 6-måneders spareabonnement på Standard med reklamer på 69 kr./md. Baseret på en bindingsperiode på 6 måneder, hvorefter abonnementet fornys automatisk til 79 kr. pr. måned. Afmeld når som helst med virkning efter bindingsperioden.',
		},
		{
			ribbon: 'Offer',
			startDate: '2026-10-08',
			endDate: '2026-10-12',
			provider: 'sst',
			offerText: 'Limited Offer',
			longText: 'Save 40% with SkyShowtime*',
			disclaimerText:
				'*Offer ends 12.10.26. 65kr/month for 6 months on Standard plan, then auto-renews at full monthly price (currently 109 kr) unless cancelled. Cancel anytime. New & eligible returning customers only. Terms apply.',
			offerDisclaimerText:
				'*Offer ends 12.10.26. 65kr/month for 6 months on Standard plan, then auto-renews at full monthly price (currently 109 kr) unless cancelled. Cancel anytime. New & eligible returning customers only. Terms apply.',
		},
	],
	ES: [
		{
			ribbon: 'Ver oferta',
			startDate: '2026-09-10',
			endDate: '2026-10-12',
			provider: 'sst',
			longText: 'desde 4,99 € al mes durante 6 meses',
			disclaimerText:
				'La oferta finaliza el 12/10/2026. Plan Estándar por 4,99 € al mes durante 6 meses. Después, la suscripción se renovará automáticamente al precio mensual completo (actualmente 8,99 €), salvo cancelación. Cancela cuando quieras; la cancelación será efectiva al final del periodo de facturación. Solo para clientes nuevos y antiguos clientes que cumplan los requisitos. Se aplican términos y condiciones.',
			offerDisclaimerText:
				'La oferta finaliza el 12/10/2026. Plan Estándar por 4,99 € al mes durante 6 meses. Después, la suscripción se renovará automáticamente al precio mensual completo (actualmente 8,99 €), salvo cancelación. Cancela cuando quieras; la cancelación será efectiva al final del periodo de facturación. Solo para clientes nuevos y antiguos clientes que cumplan los requisitos. Se aplican términos y condiciones.',
		},
	],
	FI: [
		{
			ribbon: 'Etu',
			startDate: '2026-10-06',
			endDate: '2026-10-13',
			provider: 'dnp',
			offerText: 'Toimi heti',
			longText: 'Alkaen 5,99 €/kk, kun tilaat 6 kuukaudeksi.',
			disclaimerText:
				'Tarjous päättyy 13.10.26. Tarjous on voimassa vain uusille ja ehdot täyttäville palaaville tilaajille. Tarjous on tarkoitettu vain vähintään 18-vuotiaille. Standard mainoksilla -tilauspaketin kuuden kuukauden säästöhinta nousee 14.10.26. Uusi hinta on 6,99 €/kk. Säästöprosentti on laskettu vertaamalla nykyistä hintaa 5,99 €/kk ja uuteen Standard mainoksilla -tilauspaketin 6 kuukauden säästöhintaan 6,99 €/kk. Perustuu kuuden kuukauden vähimmäisjaksoon, jonka jälkeen tilaus uusiutuu automaattisesti hintaan 7,99 € kuukaudessa. Voit peruuttaa milloin tahansa. Peruutus astuu voimaan vähimmäisajan jälkeen.',
			offerDisclaimerText:
				'Tarjous päättyy 13.10.26. Tarjous on voimassa vain uusille ja ehdot täyttäville palaaville tilaajille. Tarjous on tarkoitettu vain vähintään 18-vuotiaille. Standard mainoksilla -tilauspaketin kuuden kuukauden säästöhinta nousee 14.10.26. Uusi hinta on 6,99 €/kk. Säästöprosentti on laskettu vertaamalla nykyistä hintaa 5,99 €/kk ja uuteen Standard mainoksilla -tilauspaketin 6 kuukauden säästöhintaan 6,99 €/kk. Perustuu kuuden kuukauden vähimmäisjaksoon, jonka jälkeen tilaus uusiutuu automaattisesti hintaan 7,99 € kuukaudessa. Voit peruuttaa milloin tahansa. Peruutus astuu voimaan vähimmäisajan jälkeen.',
		},
		{
			ribbon: 'Offer',
			startDate: '2026-10-08',
			endDate: '2026-10-12',
			provider: 'sst',
			offerText: 'Limited Offer',
			longText: 'Save 40% with SkyShowtime*',
			disclaimerText:
				'*Offer ends 12.10.26. 65kr/month for 6 months on Standard plan, then auto-renews at full monthly price (currently 109 kr) unless cancelled. Cancel anytime. New & eligible returning customers only. Terms apply.',
			offerDisclaimerText:
				'*Offer ends 12.10.26. 65kr/month for 6 months on Standard plan, then auto-renews at full monthly price (currently 109 kr) unless cancelled. Cancel anytime. New & eligible returning customers only. Terms apply.',
		},
	],
	GB: [
		{
			startDate: '2026-09-28',
			endDate: '2026-10-13',
			provider: 'dnp',
			offerText: 'Last chance!',
			longText: 'Disney+ from £4.99 a month when you sign up for 6 months',
			disclaimerText:
				'Offer ends 13/10/26. New and eligible returning subscribers only. Must be 18+. The price of the Standard with Ads 6-month saver will increase to £5.99/mo from 14/10/26. Savings compares the current price of £4.99/mo against the new Standard with Ads 6-month saver price of £5.99/mo. Based on a 6-month minimum term, subscription then auto-renews at £6.99 a month. Cancel anytime, effective after minimum term.',
			offerDisclaimerText:
				'Offer ends 13/10/26. New and eligible returning subscribers only. Must be 18+. The price of the Standard with Ads 6-month saver will increase to £5.99/mo from 14/10/26. Savings compares the current price of £4.99/mo against the new Standard with Ads 6-month saver price of £5.99/mo. Based on a 6-month minimum term, subscription then auto-renews at £6.99 a month. Cancel anytime, effective after minimum term.',
		},
	],
	NO: [
		{
			ribbon: 'Deal',
			startDate: '2026-10-06',
			endDate: '2026-10-13',
			provider: 'dnp',
			offerText: 'Siste sjanse',
			longText: 'Fra 59 kr/mnd. når du registrerer deg for 6 mnd.',
			disclaimerText:
				'Tilbudet gjelder t.o.m. 13.10.2026. Gjelder bare for nye og tilbakevendende abonnenter som oppfyller kriteriene. Må være minst 18 år. Prisen for 6-månederstilbudet på Standard med reklame øker til 79 kr per måned fra 14. oktober 2026. Besparelsen er beregnet ved å sammenligne dagens pris på 59 kr per måned med den nye prisen på 79 kr per måned for 6-månederstilbudet på Standard med reklame. Etter en bindingstid på seks måneder fornyes abonnementet automatisk til 89 kr per måned. Avslutt når som helst med ikrafttredelse etter bindingstiden.',
			offerDisclaimerText:
				'Tilbudet gjelder t.o.m. 13.10.2026. Gjelder bare for nye og tilbakevendende abonnenter som oppfyller kriteriene. Må være minst 18 år. Prisen for 6-månederstilbudet på Standard med reklame øker til 79 kr per måned fra 14. oktober 2026. Besparelsen er beregnet ved å sammenligne dagens pris på 59 kr per måned med den nye prisen på 79 kr per måned for 6-månederstilbudet på Standard med reklame. Etter en bindingstid på seks måneder fornyes abonnementet automatisk til 89 kr per måned. Avslutt når som helst med ikrafttredelse etter bindingstiden.',
		},
		{
			ribbon: 'Offer',
			startDate: '2026-10-08',
			endDate: '2026-10-12',
			provider: 'sst',
			offerText: 'Limited Offer',
			longText: 'Save 40% with SkyShowtime*',
			disclaimerText:
				'*Offer ends 12.10.26. 65kr/month for 6 months on Standard plan, then auto-renews at full monthly price (currently 109 kr) unless cancelled. Cancel anytime. New & eligible returning customers only. Terms apply.',
			offerDisclaimerText:
				'*Offer ends 12.10.26. 65kr/month for 6 months on Standard plan, then auto-renews at full monthly price (currently 109 kr) unless cancelled. Cancel anytime. New & eligible returning customers only. Terms apply.',
		},
	],
	SE: [
		{
			ribbon: 'Deal',
			startDate: '2026-10-06',
			endDate: '2026-10-13',
			provider: 'dnp',
			offerText: 'Sista chansen',
			longText: 'Från 59 kr/mån när du registrerar dig för 6 mån.',
			disclaimerText:
				'Gäller till den 13 oktober 2026. Gäller endast för nya och berättigade återkommande abonnenter. Det rabatterade priset för ett abonnemang på Standard med reklam i 6 månader höjs till 79 kr/månad från och med den 14 oktober 2026. Besparingen avser det nuvarande priset på 59 kr jämfört med det nya rabatterade priset för Standard med reklam i 6 månader på 79 kr/månad. Baserat på en minimiperiod på 6 månader. Abonnemanget förnyas därefter automatiskt för 89 kr per månad. Avsluta när som helst – uppsägningen träder i kraft efter minimiperioden.',
			offerDisclaimerText:
				'Gäller till den 13 oktober 2026. Gäller endast för nya och berättigade återkommande abonnenter. Det rabatterade priset för ett abonnemang på Standard med reklam i 6 månader höjs till 79 kr/månad från och med den 14 oktober 2026. Besparingen avser det nuvarande priset på 59 kr jämfört med det nya rabatterade priset för Standard med reklam i 6 månader på 79 kr/månad. Baserat på en minimiperiod på 6 månader. Abonnemanget förnyas därefter automatiskt för 89 kr per månad. Avsluta när som helst – uppsägningen träder i kraft efter minimiperioden.',
		},
		{
			ribbon: 'Offer',
			startDate: '2026-10-08',
			endDate: '2026-10-12',
			provider: 'sst',
			offerText: 'Limited Offer',
			longText: 'Save 40% with SkyShowtime*',
			disclaimerText:
				'*Offer ends 12.10.26. 65kr/month for 6 months on Standard plan, then auto-renews at full monthly price (currently 109 kr) unless cancelled. Cancel anytime. New & eligible returning customers only. Terms apply.',
			offerDisclaimerText:
				'*Offer ends 12.10.26. 65kr/month for 6 months on Standard plan, then auto-renews at full monthly price (currently 109 kr) unless cancelled. Cancel anytime. New & eligible returning customers only. Terms apply.',
		},
	],
} satisfies Partial<Record<PromotionLocale, PromoText[]>>;

type PromotionCountries = keyof typeof promotions;
type PromotionProviders = ValueOf<typeof promotions>[number]['provider'];

const promotionProviderIcons: Record<PromotionProviders, string> = {
	amp: `${JW_CONFIG.IMAGESCALER_URL}/icon/322992749/s100/amazonprime.webp`,
	prv: `${JW_CONFIG.IMAGESCALER_URL}/icon/322992749/s100/amazonprime.webp`,
	hst: `${JW_CONFIG.IMAGESCALER_URL}/icon/324950024/s100/hotstar.webp`,
	dnp: `${JW_CONFIG.IMAGESCALER_URL}/icon/313118777/s100/disneyplus.webp`,
	atp: `${JW_CONFIG.IMAGESCALER_URL}/icon/152862153/s100/appletvplus.webp`,
	bng: `${JW_CONFIG.IMAGESCALER_URL}/icon/183470172/s100/binge.webp`,
	pmp: `${JW_CONFIG.IMAGESCALER_URL}/icon/242706661/s100/paramountplus.webp`,
	sst: `${JW_CONFIG.IMAGESCALER_URL}/icon/300772362/s100/skyshowtime.webp`,
	mxx: `${JW_CONFIG.IMAGESCALER_URL}/icon/305458112/s100/max.webp`,
	tbv: `${JW_CONFIG.IMAGESCALER_URL}/icon/313528601/s100/tubitv.webp`,
	cts: `${JW_CONFIG.IMAGESCALER_URL}/icon/48600036/s100/curiositystream.webp`,
	aho: `${JW_CONFIG.IMAGESCALER_URL}/icon/343788557/s100/amazonhbomax.webp`,
};

/** SSR Safe way to get active provider promotions. */
export function useProviderPromotion(title?: MaybeRefOrGetter<TitleDetail>): {
	titleHasProviderPromotion: ComputedRef<boolean>;
	titleHasBackdropPromo: ComputedRef<boolean>;
	isPromotionOffer: (offer: TitleOfferFragment) => boolean;
	providerPromotion: Ref<PromoTextWithIcon | null>;
} {
	const titleDetailsStore = useTitleDetailsStore();
	const { setProviderPromotion } = titleDetailsStore;
	const { providerPromotion } = storeToRefs(titleDetailsStore);

	const { activeVariantWithControlGroup: primePlayPromoVariant } = useExperiment('EXP_PRIME_PLAY_PROMO');

	onServerPrefetch(() => setProviderPromotion(toValue(title)));

	// refetch on client (no hydration error) to get the updated status
	onMounted(() => setProviderPromotion(toValue(title)));

	const titleHasProviderPromotion = computed(() => providerPromotion.value != null);
	const titleHasBackdropPromo = computed(() => {
		if (!titleHasProviderPromotion.value || providerPromotion.value?.hideBackdropPromo) return false;
		if (providerPromotion.value?.experimentName === 'EXP_PRIME_PLAY_PROMO')
			return primePlayPromoVariant.value === 'variant_1';
		return true;
	});

	function isPromotionOffer(offer: TitleOfferFragment) {
		return (
			flatrateOrFreeOrAds.includes(offer.monetizationType) &&
			(providerPromotion.value as PromoTextWithIcon | null)?.provider === offer.package.shortName
		);
	}

	return {
		/** Is a provider promotion applicable to the current title - typically be play button (backdrop) promotion, or updating offer texts and ribbons. */
		titleHasProviderPromotion,

		/** Is a provider promotion applicable to the backdrop play button. */
		titleHasBackdropPromo,

		/** Check if the offer is under the current title's provider promotion. */
		isPromotionOffer,

		/** The applicable promotion itself. */
		providerPromotion,
	};
}

export function getPromoText(
	country: string,
	title: TitleDetail | null = null,
	currentDate: Date = new Date()
): PromoTextWithIcon | null {
	const promos = promotions[country as PromotionCountries];

	if (!promos || promos.length === 0 || title == null || title.offers.length === 0) return null;

	const promoText = promos.find(promo =>
		title.offers.some(
			offer => flatrateOrFreeOrAds.includes(offer.monetizationType) && offer.package.shortName === promo.provider
		)
	);

	if (promoText == null) return null;
	// Checking if we're within the promo dates
	if (promoText.startDate && +currentDate < new Date(promoText.startDate).setUTCHours(0, 0, 0, 0)) return null;
	if (promoText.endDate && +currentDate > new Date(promoText.endDate).setUTCHours(23, 59, 59, 999)) return null;

	return { ...promoText, icon: promotionProviderIcons[promoText.provider] };
}
